import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";
import { Plus } from "lucide-react";
import { OnboardingSuccessDialog } from "@/components/certs/OnboardingSuccessDialog";
import { successFromDp } from "@/lib/certs";

const CHECK = [["aadhaar", "Aadhaar"], ["pan", "PAN"], ["dl", "Driving Licence"], ["bank", "Bank details"], ["photo", "Profile photo"], ["agreement", "Agreement"], ["training", "Training"], ["bag", "Bag issued"], ["tshirt", "T-Shirt issued"], ["ev", "EV issued"], ["platform_activated", "Platform activated"]];

export function NewDPDialog({ open, onClose, onSaved, locs = [] }) {
  const { role } = useApp();
  const [f, setF] = useState({});
  const [frQuery, setFrQuery] = useState("");
  const [frResults, setFrResults] = useState([]);
  const [models, setModels] = useState([]);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  useEffect(() => {
    if (open) api.get("/earning-models/active").then(({ data }) => setModels(data)).catch(() => {});
  }, [open]);
  useEffect(() => {
    const t = setTimeout(() => { if (frQuery) api.get(`/franchise/search?q=${encodeURIComponent(frQuery)}`).then(({ data }) => setFrResults(data)); else setFrResults([]); }, 200);
    return () => clearTimeout(t);
  }, [frQuery]);
  const save = async () => {
    if (!f.full_name || !f.fe_id) { toast.error("Name and FE ID required"); return; }
    if (!f.earning_model_id) { toast.error("Please select an Earning Model"); return; }
    try {
      const payload = { ...f, earning_model_name: (models.find((m) => m.id === f.earning_model_id) || {}).name || "", _user: role };
      const { data: created } = await api.post("/dp", payload);
      toast.success("Delivery Partner onboarded"); onSaved?.(created); onClose(); setF({}); setFrQuery("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto bg-card" data-testid="new-dp-dialog">
        <DialogHeader><DialogTitle className="font-head">Onboard Delivery Partner</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Full Name *</Label><Input value={f.full_name || ""} onChange={(e) => set("full_name", e.target.value)} data-testid="new-dp-name" /></div>
          <div><Label>FE ID *</Label><Input value={f.fe_id || ""} onChange={(e) => set("fe_id", e.target.value)} data-testid="new-dp-feid" /></div>
          <div><Label>Mobile</Label><Input value={f.mobile || ""} onChange={(e) => set("mobile", e.target.value)} /></div>
          <div><Label>Platform</Label><Select value={f.platform} onValueChange={(v) => set("platform", v)}><SelectTrigger><SelectValue placeholder="Zomato" /></SelectTrigger><SelectContent>{["Zomato", "Swiggy", "Blinkit", "Zepto"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select></div>
          <div><Label>Location</Label><Select value={f.location} onValueChange={(v) => set("location", v)}><SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{locs.map((l) => <SelectItem key={l.name} value={l.name}>{l.name}</SelectItem>)}</SelectContent></Select></div>
        </div>
        <div className="mt-3">
          <p className="mb-2 border-b border-border pb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Earnings & Commercial Details</p>
          <div className="grid grid-cols-1 gap-3">
            <div>
              <Label>Earning Model *</Label>
              <Select value={f.earning_model_id || ""} onValueChange={(v) => set("earning_model_id", v)}>
                <SelectTrigger data-testid="new-dp-earning-model"><SelectValue placeholder="Select Earning Model" /></SelectTrigger>
                <SelectContent>
                  {models.length === 0 && <div className="px-3 py-2 text-xs text-muted-foreground">No active models — create one in Settings → Earning Models</div>}
                  {models.map((m) => <SelectItem key={m.id} value={m.id} data-testid={`model-option-${m.id}`}>{m.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <div className="mt-3">
          <Label>Under Franchise Partner</Label>
          {f.franchise_partner_id ? (
            <div className="flex items-center justify-between rounded-lg border border-border bg-secondary/40 px-3 py-2 text-sm" data-testid="selected-franchise">
              <span><span className="font-semibold">{f.franchise_partner_name}</span> <span className="font-mono text-xs text-muted-foreground">{f.franchise_referral}</span></span>
              <button className="text-xs text-rose-500 hover:underline" onClick={() => setF((s) => ({ ...s, franchise_partner_id: null, franchise_partner_name: "", franchise_referral: "" }))}>Change</button>
            </div>
          ) : (
            <>
              <Input placeholder="Search name / referral / contact…" value={frQuery} onChange={(e) => setFrQuery(e.target.value)} data-testid="franchise-select-search" />
              {frResults.length > 0 && (
                <div className="mt-1 max-h-40 overflow-y-auto rounded-lg border border-border bg-popover">
                  {frResults.map((fr) => (
                    <button key={fr.id} data-testid={`franchise-option-${fr.referral_number}`} onClick={() => { setF((s) => ({ ...s, franchise_partner_id: fr.id, franchise_partner_name: fr.name, franchise_referral: fr.referral_number })); setFrResults([]); setFrQuery(""); }}
                      className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-accent">
                      <span>{fr.name} <span className="font-mono text-xs text-muted-foreground">{fr.referral_number}</span></span>
                      <span className="text-xs text-muted-foreground">{fr.city}</span>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={save} data-testid="save-new-dp">Onboard</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Onboarding() {
  const [dps, setDps] = useState([]);
  const [locs, setLocs] = useState([]);
  const [open, setOpen] = useState(false);
  const [done, setDone] = useState(null);
  const nav = useNavigate();
  const load = () => api.get("/dp").then(({ data }) => setDps(data));
  useEffect(() => { load(); api.get("/locations").then(({ data }) => setLocs(data)); }, []);

  const stages = ["New Lead", "Documents Pending", "KYC Under Review", "KYC Approved", "Training Pending", "Ready to Work", "Active"];
  const byStage = stages.map((s) => ({ stage: s, count: dps.filter((d) => d.onboarding_status === s).length }));

  return (
    <div>
      <PageHeader title="Onboarding" subtitle="Delivery partner onboarding pipeline & checklist progress">
        <Button onClick={() => setOpen(true)} data-testid="onboard-dp-btn"><Plus className="mr-1 h-4 w-4" /> New Onboarding</Button>
      </PageHeader>
      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7">
        {byStage.map((s) => <div key={s.stage} className="rounded-xl border border-border bg-card p-3 text-center"><p className="font-head text-xl font-bold">{s.count}</p><p className="text-[11px] text-muted-foreground">{s.stage}</p></div>)}
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {dps.map((d) => (
          <button key={d.id} onClick={() => nav(`/dp/${d.id}`)} className="rounded-xl border border-border bg-card p-4 text-left transition hover:border-primary/40" data-testid={`onboarding-card-${d.fe_id}`}>
            <div className="flex items-center justify-between"><p className="font-semibold">{d.full_name}</p><StatusBadge status={d.status} /></div>
            <p className="text-xs text-muted-foreground font-mono">{d.fe_id} · {d.onboarding_status}</p>
            <div className="mt-3 flex items-center gap-2"><Progress value={d.onboarding_progress} className="h-2" /><span className="text-xs font-semibold tabular">{d.onboarding_progress}%</span></div>
            <p className="mt-2 text-xs text-muted-foreground">KYC: {d.kyc_status}</p>
          </button>
        ))}
      </div>
      <NewDPDialog open={open} onClose={() => setOpen(false)} onSaved={(c) => { load(); if (c?.id) setDone(successFromDp(c)); }} locs={locs} />
      <OnboardingSuccessDialog partner={done} onClose={() => setDone(null)} />
    </div>
  );
}
